import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiltersBase as Filters,
  IFiltersProps,
  IFilterable,
  IOnFilterChangeProps,
} from '@glu/data-components';
import { useComposedGridContext } from '../ComposedGrid/ComposedGridContext';
import { convertFilter, createAgGridFilter, getFilterable } from './utils';

interface IUseFiltersAgGridProps {
  /** Callback to close the drawer */
  onClose(): void;
}

export interface IFiltersAgGridProps extends Partial<IFiltersProps>, IUseFiltersAgGridProps {}

const useFiltersAgGrid = ({ onClose }: IUseFiltersAgGridProps) => {
  const { gridRef } = useComposedGridContext();
  const [localFilters, setLocalFilters] = useState(gridRef.current.api.getFilterModel());

  const filterableColumns = useMemo(() => {
    return gridRef.current.columnApi.getColumnState().reduce<IFilterable[]>((colArray, column) => {
      const field = column.colId;
      const currentColumn = gridRef.current.columnApi.getColumn(field)!;
      const columnDef = currentColumn.getColDef()!;
      if (!columnDef.filter) {
        return colArray;
      }

      return [
        ...colArray,
        getFilterable({
          api: gridRef.current.api,
          column: currentColumn,
          columnDef,
          displayName: gridRef.current.columnApi.getDisplayNameForColumn(currentColumn, null),
        }),
      ];
    }, []);
  }, [gridRef]);

  const onChange = useCallback((filterParams: IOnFilterChangeProps) => {
    const { filterable, value } = filterParams;
    const { componentType, filterOptions } = filterable;
    const { inRangeInclusive } = filterOptions || {};
    const newFilter = createAgGridFilter({ componentType, inRangeInclusive, value } as any);
    setLocalFilters((currentFilters) => ({
      ...currentFilters,
      [filterable.field]: newFilter,
    }));
  }, []);

  const onClear = useCallback((name: string) => {
    setLocalFilters((currentFilters) => {
      return Object.keys(currentFilters).reduce((newFilters, filterName) => {
        if (filterName === name) {
          return newFilters;
        }
        return { ...newFilters, [filterName]: currentFilters[filterName] };
      }, {});
    });
  }, []);

  const onCancel = useCallback(() => {
    const currentFilters = gridRef.current.api.getFilterModel();
    setLocalFilters(currentFilters);
    onClose();
  }, [gridRef, onClose]);

  const onApply = useCallback(() => {
    gridRef.current.api.setFilterModel(localFilters);
    onClose();
  }, [gridRef, localFilters, onClose]);

  const parsedFilters = useMemo(
    () =>
      Object.entries(localFilters).reduce<IFiltersProps['filterValues']>(
        (newFilters, [field, filter]) => ({
          ...newFilters,
          [field]: convertFilter(filter),
        }),
        {}
      ),
    [localFilters]
  );

  useEffect(() => {
    let isMounted = true;
    const currRef = gridRef.current;
    const setNewState = () => {
      if (!isMounted) return;
      setLocalFilters(currRef.api.getFilterModel());
    };
    currRef.api.addEventListener('filterChanged', setNewState);
    return () => {
      isMounted = false;
      currRef.api.removeEventListener('filterChanged', setNewState);
    };
  }, [gridRef]);

  return useMemo(
    () => ({
      filterables: filterableColumns,
      filterValues: parsedFilters,
      onApply,
      onCancel,
      onFilterChange: onChange,
      onClear,
    }),
    [filterableColumns, onApply, onCancel, onChange, onClear, parsedFilters]
  );
};

const FiltersAgGrid = ({ onClose, ...props }: IFiltersAgGridProps) => {
  const propsFromHook = useFiltersAgGrid({ onClose });

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Filters {...propsFromHook} {...props} />;
};

export default FiltersAgGrid;
